/**
 * The resolved view over the document — the read side of ADR 0012.
 *
 * `src/storage/relationship-resolver.ts` folded in here (#373), and #291 then made
 * `calculateLastInteraction` agree with its neighbours about archived entries.
 *
 * **`resolveLinks` is the one place a link is walked, and the one place ADR 0012's rule 5 holds.**
 * Its callers ask for an entry's neighbours and filter what comes back; none of them touches
 * `sourceId` or `targetId` itself, which is what made #291 a change to one function rather than to
 * four.
 *
 * Two signature shapes sit side by side on purpose. `resolveLinks` is new interface and takes the
 * view first, the way ADR 0012 shapes every `select…(view: DocumentView, …)`. The four readers
 * folded in keep `(subjectId, view)`, the argument order they had at their old path, which is what
 * let their six importers absorb the fold by changing one string. They take the new order when
 * step 2 rewrites them into selectors.
 */

import type { AppDocument, Entry, Link } from './schema'

// ============================================================================
// The view
// ============================================================================

/**
 * The document as the read side sees it — what `useDocumentView()` returns (#374).
 *
 * It is the whole document, read-only. It was narrower at #373, when its only readers were the
 * four below and they read two containers between them; #374 moved 49 sites onto it and they read
 * every container, so narrowing it further would only be a lie about who reads what.
 *
 * **It carries no indexes yet.** ADR 0012 describes a resolved view carrying the link and topic
 * indexes every surface rebuilds today. Building them is not this: the surfaces that would use them
 * are the query hooks, which become `select…(view, …)` functions in step 2, and an index with no
 * reader is a cost with no payer. This type is the name they will be built behind.
 */
export type DocumentView = Readonly<AppDocument>

// ============================================================================
// The link primitive
// ============================================================================

/**
 * Resolve every entry linked to `entryId`, in either direction.
 *
 * Deduplicated by entry id, in the order the links are walked. A link is skipped when its other
 * endpoint names no entry, and when that endpoint is archived: **a link whose other endpoint is
 * archived is not a live relationship** (ADR 0012, rule 5). Every "delete" in this app writes
 * `archivedAt` rather than removing anything, so this is the rule that makes a deleted entry stop
 * counting.
 *
 * The rule is held on read rather than on write, and it has to be. Archiving an entry and dropping
 * its links is not an invariant a multi-writer document can hold: one device archiving while
 * another tags a person on the same entry merges to an archived entry holding a live link, since
 * Automerge maps merge per key. A read rule converges by construction (ADR 0012:159-169).
 */
export function resolveLinks(
  view: DocumentView | null,
  entryId: string,
): Entry[] {
  if (!view || !view.links || !view.entries) return []

  const neighbourIds = new Set<string>()

  Object.values(view.links).forEach((link: Link) => {
    if (link.sourceId === entryId) neighbourIds.add(link.targetId)
    if (link.targetId === entryId) neighbourIds.add(link.sourceId)
  })

  const entries: Entry[] = []
  neighbourIds.forEach((id) => {
    const entry = view.entries[id]
    if (entry && !entry.archivedAt) entries.push(entry)
  })

  return entries
}

/**
 * A person entry that has not been archived.
 *
 * Module-private, and the successor to the old `getPersonById`: every caller that resolved a
 * person id to an entry now filters entries `resolveLinks` already resolved.
 */
function isLivePerson(entry: Entry): boolean {
  return entry.type === 'person' && !entry.archivedAt
}

// ============================================================================
// People
// ============================================================================

/**
 * The people linked to a single entry.
 *
 * @param entryId Entry ID to find people for
 * @param view The document
 * @returns Person entries linked to this entry, archived people excluded
 */
export function resolveLinkedPeople(
  entryId: string,
  view: DocumentView | null,
): Entry[] {
  return resolveLinks(view, entryId).filter(isLivePerson)
}

/**
 * The people linked to each of many entries, walking the links once.
 *
 * The batched counterpart to `resolveLinkedPeople`, kept through the fold despite having no caller
 * yet: it is what #290 adopts to collapse the six per-entry projections in the query hooks, and
 * dropping it would mean writing it again in step 2. It does not route through `resolveLinks`,
 * because doing so per entry is what it exists to avoid.
 *
 * @param entryIds Entry IDs to find people for
 * @param view The document
 * @returns Map of entryId to its linked person entries, archived people excluded
 */
export function resolvePeopleForEntries(
  entryIds: string[],
  view: DocumentView | null,
): Map<string, Entry[]> {
  const result = new Map<string, Entry[]>()

  // Every requested entry gets an answer, even when there is no document to answer from
  entryIds.forEach((id) => result.set(id, []))

  if (!view || !view.links || !view.entries) return result

  const entryToPeople = new Map<string, Set<string>>()
  entryIds.forEach((id) => entryToPeople.set(id, new Set()))

  // One pass over the links, rather than one pass per entry
  Object.values(view.links).forEach((link: Link) => {
    if (entryToPeople.has(link.sourceId)) {
      const targetEntry = view.entries[link.targetId]
      if (targetEntry && isLivePerson(targetEntry)) {
        entryToPeople.get(link.sourceId)!.add(link.targetId)
      }
    }

    if (entryToPeople.has(link.targetId)) {
      const sourceEntry = view.entries[link.sourceId]
      if (sourceEntry && isLivePerson(sourceEntry)) {
        entryToPeople.get(link.targetId)!.add(link.sourceId)
      }
    }
  })

  entryToPeople.forEach((personIds, entryId) => {
    const people: Entry[] = []
    personIds.forEach((personId) => {
      const person = view.entries[personId]
      if (person && isLivePerson(person)) people.push(person)
    })
    result.set(entryId, people)
  })

  return result
}

/**
 * The date of a person's most recent interaction.
 *
 * Archived entries do not count, because `resolveLinks` does not return them. Until #291 this
 * reader was the one link reader in the module with no `archivedAt` check, so deleting the only
 * task linked to a person left their last-interaction date advancing anyway — the people list
 * sorted them to the top, the person panel printed a date against an entry the user had removed,
 * and the dashboard nudge that should have called them overdue stayed silent.
 *
 * A person whose linked entries are all archived now returns `null`, which both readers already
 * handle: the people list sorts them to the end by title (`usePeopleData.ts:46-49`) and the nudges
 * read `null` as very overdue (`usePeopleNudges.ts:57-58`).
 *
 * @param personId Person entry ID
 * @param view The document
 * @returns ISO date string of the last interaction, or null if there is none
 */
export function calculateLastInteraction(
  personId: string,
  view: DocumentView | null,
): string | null {
  let lastDate: string | null = null

  for (const entry of resolveLinks(view, personId)) {
    if (entry.type === 'person') continue
    // ISO strings compare lexicographically
    if (lastDate === null || entry.createdAt > lastDate) {
      lastDate = entry.createdAt
    }
  }

  return lastDate
}

// ============================================================================
// Entries
// ============================================================================

/**
 * The entries linked to a person, for the person detail page.
 *
 * It filters people, not archived entries: `resolveLinks` holds that rule now, and repeating it
 * here would put a second copy of it where the ADR asks for one.
 *
 * @param personId Person entry ID
 * @param view The document
 * @returns Linked entries, excluding people and archived entries
 */
export function getEntriesForPerson(
  personId: string,
  view: DocumentView | null,
): Entry[] {
  return resolveLinks(view, personId).filter((entry) => entry.type !== 'person')
}
